import { useMemo, useState } from "react";
import { Button } from "../../../ui";
import { getUnifiedRequestAPI } from "../../../../utils/api/unifiedRequests";
import ActionWorkflowButtons from "../../../operation/campaignActions/ActionWorkflowButtons";
import { cancelDashboardRequestAPI, reviewDashboardRequestAPI } from "../../../../utils/api/ludDashboard";
import { cancelChangeRequestAPI, completeSubscriptionAPI, reviewChangeRequestAPI, subscriptionActionAPI } from "../../../../utils/api/applications";
import ActionDialog from "./ActionDialog";

// The source-specific actions of one request. Every button opens a
// confirmation and then calls the ORIGINAL endpoint of that request system
// (no generic approve / send / execute exists): authorization and state checks
// stay on the server, a refusal is shown as its message. Which buttons appear
// comes from the unified API's `actions` for the current viewer.
//
// The quick actions of a row for the requests that have no dedicated row workflow
// (Operations, application changes and subscriptions, other dashboard requests).
// Each button opens the confirmation of the original workflow; nothing is executed
// without it. The typed requests (statement, tax invoice, contract, account manager,
// maintenance) use RowActions / BranchQuickActions.
export default function RequestActions({ request, t, lang, onDone, empty = null }) {
  const token = localStorage.getItem("auth-token");
  const [dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState("");
  const source = request.identity.source;
  const id = request.identity.id;
  const storeId = request.scope?.storeId;
  const a = request.actions || {};

  const finish = async () => {
    setDialog(null);
    await onDone?.();
  };

  const descriptors = useMemo(() => {
    const list = [];
    const add = (descriptor) => list.push(descriptor);
    const status = request.status?.native;

    if (source === "dashboard") {
      if (a.review) {
        add({ key: "approve", label: t.action.approve, variant: "success", description: t.action.approveDashboard, run: async () => reviewDashboardRequestAPI(token, id, "approved", "") });
        add({ key: "reject", label: t.action.reject, variant: "danger", reason: { label: t.action.optionalNote, required: false }, run: async (note) => reviewDashboardRequestAPI(token, id, "rejected", note) });
      }
      if (a.cancel) add({ key: "cancel", label: t.action.cancel, variant: "danger", reason: { label: t.action.optionalNote, required: false }, run: async (note) => cancelDashboardRequestAPI(token, id, note) });
    }

    if (source === "appChange") {
      if (a.review) {
        add({ key: "approve", label: t.action.approve, variant: "success", run: async () => reviewChangeRequestAPI(token, id, { decision: "approve", version: request.details?.version }) });
        add({ key: "reject", label: t.action.reject, variant: "danger", reason: { label: t.action.reasonLabel, required: true }, run: async (reason) => reviewChangeRequestAPI(token, id, { decision: "reject", version: request.details?.version, reason }) });
      }
      if (a.cancel) add({ key: "cancel", label: t.action.cancel, variant: "danger", run: async () => cancelChangeRequestAPI(token, storeId, id) });
    }

    if (source === "appSubscription") {
      const email = request.details?.email;
      if (a.send) {
        add({
          key: "send",
          label: status === "failed" ? t.action.retry : t.action.send,
          variant: "primary",
          preview: email,
          needsDetails: true,
          run: async () => subscriptionActionAPI(token, storeId, id, "send"),
        });
      }
      if (a.cancel) add({ key: "cancel", label: t.action.cancel, variant: "danger", reason: { label: t.action.optionalNote, required: false }, run: async (note) => subscriptionActionAPI(token, storeId, id, "cancel", note ? { note } : {}) });
      if (a.complete) add({ key: "complete", label: t.action.complete, variant: "success", reason: { label: t.action.optionalNote, required: false }, run: async (note) => completeSubscriptionAPI(token, id, note) });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, t, token]);

  // Operations actions: the one workflow component, driven by the flags the unified API
  // computed from the same definition Operations uses (Update loads the full action on demand)
  if (source === "operations") {
    const open = ["pending", "inprogress", "active"].includes(request.status?.native);
    const allowed = {
      stage: open ? 1 : null,
      approveAsClient: a.approveAsClient, rejectAsClient: a.rejectAsClient, approveAsManager: a.approveAsManager, rejectAsManager: a.rejectAsManager, approveOnBehalf: a.approveOnBehalf,
      activate: (a.changeStatusTo || []).includes("active"), complete: (a.changeStatusTo || []).includes("completed"), cancel: a.cancel, delete: a.deleteRequest, edit: a.edit,
    };
    const loadAction = async () => {
      const full = await getUnifiedRequestAPI(token, source, id);
      const d = full.details || {};
      return { _id: id, storeId: full.scope?.storeId, status: full.status?.native, source: d.source, campaignType: d.campaignType, campaignName: d.campaignName, campaignDetails: d.campaignDetails, startDate: d.startDate, endDate: d.endDate, content: full.summary };
    };
    return <ActionWorkflowButtons id={id} allowed={allowed} loadAction={loadAction} lang={lang} onDone={onDone} empty={empty} />;
  }

  // the stored e-mail of a subscription is shown in the confirmation: it is fetched when needed
  const open = async (descriptor) => {
    if (!descriptor.needsDetails) {
      setDialog(descriptor);
      return;
    }
    setLoading(descriptor.key);
    try {
      const full = await getUnifiedRequestAPI(token, source, id);
      setDialog({ ...descriptor, preview: full?.details?.email || descriptor.preview });
    } catch {
      setDialog(descriptor);
    } finally {
      setLoading("");
    }
  };

  if (!descriptors.length) return empty;
  return (
    <>
      {descriptors.map((descriptor) => (
        <Button key={descriptor.key} size="sm" variant={descriptor.variant} appearance={descriptor.variant === "success" || descriptor.variant === "primary" ? "filled" : "soft"} loading={loading === descriptor.key} disabled={Boolean(loading)} onClick={() => open(descriptor)}>
          {descriptor.label}
        </Button>
      ))}
      {dialog && (
        <ActionDialog
          t={t}
          title={t.action.statusTitle(dialog.label)}
          description={dialog.description || t.action.confirmText}
          reason={dialog.reason}
          ack={dialog.ack}
          confirmLabel={dialog.label}
          danger={dialog.variant === "danger"}
          onClose={() => setDialog(null)}
          onConfirm={async (reason) => {
            await dialog.run(reason);
            await finish();
          }}
        >
          {dialog.preview && (
            <div className="lud-ur-preview">
              <p>{t.action.sendNote}</p>
              <dl>
                <dt>{t.action.subject}</dt>
                <dd dir="auto">{dialog.preview.subject || t.noValue}</dd>
                <dt>{t.action.to}</dt>
                <dd dir="ltr">{(dialog.preview.to || []).join(", ") || t.noValue}</dd>
                <dt>{t.action.cc}</dt>
                <dd dir="ltr">{(dialog.preview.cc || []).join(", ") || t.noValue}</dd>
              </dl>
            </div>
          )}
        </ActionDialog>
      )}
    </>
  );
}
